import { CareOSLoader } from '@/components/ui/careos-loader';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { usePatientRole } from '@/hooks/usePatientRole';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Package, 
  Plus, 
  AlertTriangle, 
  AlertCircle,
  ChevronRight,
  Pill,
  Droplets,
  Utensils,
  Stethoscope,
  Wrench,
  CheckCircle2
} from 'lucide-react';
import type { InventoryItem, ItemType, StockStatus } from '@/types/inventory';

const itemTypeIconMap: Record<ItemType, React.ReactNode> = {
  medication: <Pill className="w-5 h-5" />,
  hygiene: <Droplets className="w-5 h-5" />,
  nutrition: <Utensils className="w-5 h-5" />,
  medical_supply: <Stethoscope className="w-5 h-5" />,
  equipment: <Wrench className="w-5 h-5" />,
  other: <Package className="w-5 h-5" />
};

export default function InventoryList() {
  const { t } = useTranslation();
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { canEdit, canEditInventory, isPatientActive } = usePatientRole(patientId);
  
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('all');

  const canManageInventory = (canEdit || canEditInventory) && isPatientActive;

  const statusConfig: Record<StockStatus, { label: string; icon: React.ReactNode; colors: string }> = {
    ok: { 
      label: t('inventory.status.ok'), 
      icon: <CheckCircle2 className="w-4 h-4" />, 
      colors: 'bg-success/10 text-success border-success/50' 
    },
    low_stock: { 
      label: t('inventory.status.low_stock'), 
      icon: <AlertTriangle className="w-4 h-4" />, 
      colors: 'bg-warning/10 text-warning border-warning/50' 
    },
    zero_stock: { 
      label: t('inventory.status.zero_stock'), 
      icon: <AlertCircle className="w-4 h-4" />, 
      colors: 'bg-destructive/10 text-destructive border-destructive/50' 
    }
  };

  useEffect(() => {
    if (patientId) {
      fetchInventory();
    }
  }, [patientId]);

  const fetchInventory = async () => {
    try {
      const { data, error } = await supabase
        .from('inventory')
        .select('*')
        .eq('patient_id', patientId)
        .order('status', { ascending: false })
        .order('item_name', { ascending: true });

      if (error) throw error;
      setInventory((data || []) as InventoryItem[]);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredInventory = activeTab === 'all' 
    ? inventory 
    : inventory.filter(item => item.item_type === activeTab);

  const alertItems = filteredInventory.filter(item => item.status !== 'ok');
  const okItems = filteredInventory.filter(item => item.status === 'ok');

  const alertCount = inventory.filter(item => item.status !== 'ok').length;

  if (isLoading) {
    return (
      <AppLayout title={t('inventory.title')} showBack onBack={() => navigate(`/app/patients/${patientId}`)}>
        <div className="flex items-center justify-center h-64">
          <CareOSLoader size="lg" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout 
      title={t('inventory.title')} 
      showBack 
      onBack={() => navigate(`/app/patients/${patientId}`)}
    >
      <div className="p-4 space-y-4 pb-24">
        {/* Alert Banner */}
        {alertCount > 0 && (
          <Card className="border-warning bg-warning/10">
            <CardContent className="p-3 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-warning" />
              <span className="text-sm font-medium">
                {alertCount} {alertCount === 1 ? t('inventory.itemsNeedAttention') : t('inventory.itemsNeedAttentionPlural')}
              </span>
            </CardContent>
          </Card>
        )}

        {/* Add Button - Only for owner */}
        {canManageInventory && (
          <Button 
            className="w-full"
            onClick={() => navigate(`/app/inventory/${patientId}/add`)}
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('inventory.addItem')}
          </Button>
        )}

        {/* Category Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full grid grid-cols-4">
            <TabsTrigger value="all" className="text-xs">
              {t('inventory.all')} ({inventory.length})
            </TabsTrigger>
            <TabsTrigger value="medication" className="text-xs">
              <Pill className="w-3 h-3 mr-1" />
              Med
            </TabsTrigger>
            <TabsTrigger value="hygiene" className="text-xs">
              <Droplets className="w-3 h-3 mr-1" />
              Hig
            </TabsTrigger>
            <TabsTrigger value="nutrition" className="text-xs">
              <Utensils className="w-3 h-3 mr-1" />
              Nut
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4 space-y-4">
            {/* Alert Items */}
            {alertItems.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-warning" />
                  <h2 className="text-sm font-semibold text-warning">{t('inventory.needAttention')}</h2>
                </div>
                <div className="space-y-2">
                  {alertItems.map(item => {
                    const config = statusConfig[item.status];
                    return (
                      <Card 
                        key={item.id}
                        className={`cursor-pointer hover:bg-accent/50 transition-colors border ${item.status === 'zero_stock' ? 'border-destructive/50' : 'border-warning/50'}`}
                        onClick={() => navigate(`/app/inventory/${patientId}/item/${item.id}`)}
                      >
                        <CardContent className="p-4 pt-4">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-full shrink-0 self-center ${config.colors}`}>
                              {itemTypeIconMap[item.item_type]}
                            </div>
                            <div className="flex-1 min-w-0 flex items-center">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate text-sm">{item.item_name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {item.unit}
                                  {item.notes && ` • ${item.notes}`}
                                </p>
                              </div>
                              <Badge variant="outline" className={`shrink-0 ml-2 ${config.colors}`}>
                                {config.icon}
                                <span className="ml-1">{config.label}</span>
                              </Badge>
                            </div>
                            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 self-center" />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* OK Items */}
            {okItems.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-4 h-4 text-success" />
                  <h2 className="text-sm font-semibold">{t('inventory.inStock')}</h2>
                </div>
                <div className="space-y-2">
                  {okItems.map(item => (
                    <Card 
                      key={item.id}
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => navigate(`/app/inventory/${patientId}/item/${item.id}`)}
                    >
                      <CardContent className="p-4 pt-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-full bg-success/10 text-success shrink-0 self-center">
                            {itemTypeIconMap[item.item_type]}
                          </div>
                          <div className="flex-1 min-w-0 flex items-center">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate text-sm">{item.item_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {item.unit}
                                {item.notes && ` • ${item.notes}`}
                              </p>
                            </div>
                            <Badge variant="outline" className="bg-success/10 text-success border-success/50 shrink-0 ml-2">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              {t('inventory.status.ok')}
                            </Badge>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 self-center" />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {filteredInventory.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center">
                  <Package className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <h3 className="font-semibold mb-2">{t('inventory.noItems')}</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {t('inventory.noItemsDescription')}
                  </p>
                  {canManageInventory && (
                    <Button onClick={() => navigate(`/app/inventory/${patientId}/add`)}>
                      <Plus className="w-4 h-4 mr-2" />
                      {t('inventory.addItem')}
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}